import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Loader2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Tooltip } from './Hover';

/**
 * The extra jobs one person does.
 *
 * A small ISP does not have one person per function: the technician who splices
 * a drop in the morning signs up the neighbour who watched him do it. Before
 * this panel the system made him choose, and in practice that meant two
 * accounts and a shared password.
 *
 * Three things are on screen at once, deliberately:
 *
 *   - **what they can do now** — the effective roles, primary first
 *   - **what each switch would add** — in capabilities, not in role names,
 *     because "also sales" tells an admin nothing and "adds seeing every
 *     customer's contact details" tells them quite a lot
 *   - **why a switch is off** — the server's own sentence, not the console's
 *     guess at one
 *
 * That last point is the reason this reads `available` from the server rather
 * than working it out here. The refusal an admin reads next to a disabled
 * checkbox is the identical string the POST would have refused with, so the two
 * cannot drift apart as the rules change.
 */
export function RoleAssignment({ userId, name }: { userId: string; name: string }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const access = useQuery({
    queryKey: ['user-access', userId],
    queryFn: () => api.userAccess(userId),
  });

  const refresh = async () => {
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['user-access', userId] }),
      // The list carries the same badges, so it goes stale on every change here.
      queryClient.invalidateQueries({ queryKey: ['users'] }),
    ]);
  };

  const grant = useMutation({
    mutationFn: (role: string) => api.grantRole(userId, role, note || undefined),
    onSuccess: async () => {
      setNote('');
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const revoke = useMutation({
    mutationFn: (role: string) => api.revokeRole(userId, role),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  if (access.isPending) {
    return (
      <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Reading what {name} can do
      </p>
    );
  }

  if (access.isError || !access.data) {
    return <p className="py-4 text-sm text-danger-600">{(access.error as Error)?.message}</p>;
  }

  const { user, directGrants, groups, effectiveRoles, capabilities, available } = access.data;
  const busy = grant.isPending || revoke.isPending;

  return (
    <div className="space-y-5 text-left">
      {/* What they can do now, before anything about changing it. */}
      <div>
        <p className="text-xs font-semibold label-mono tracking-[0.12em] text-muted-foreground">
          Does the job of
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {effectiveRoles.map((role, index) => (
            <span
              key={role}
              className={`badge ${
                index === 0 ? 'bg-navy-50 text-navy-700' : 'bg-muted text-muted-foreground'
              }`}
            >
              {role}
              {index === 0 && <span className="ml-1 text-[10px]">main</span>}
            </span>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {capabilities.length} things they may do across {effectiveRoles.length}{' '}
          {effectiveRoles.length === 1 ? 'role' : 'roles'}.
        </p>
      </div>

      {/* The switches. */}
      <div>
        <p className="text-xs font-semibold label-mono tracking-[0.12em] text-muted-foreground">
          Add another job
        </p>

        <ul className="mt-2 space-y-2">
          {available.map((option) => {
            const held = directGrants.some((entry) => entry.role === option.role);
            const fromGroup = groups.some(
              (group) => group.isActive && group.roles.includes(option.role),
            );
            const isPrimary = user.role === option.role;

            /*
             * Three ways to hold a role and only one of them can be switched
             * off here. A role that arrived with a group is removed by leaving
             * the group; a primary role is changed on the row above. Showing a
             * switch that cannot work is worse than showing none.
             */
            if (isPrimary) {
              return (
                <li key={option.role} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-navy-700" aria-hidden="true" />
                  <span>
                    <span className="font-medium">{option.role}</span>
                    <span className="block text-xs text-muted-foreground">
                      Their main job. Change it in the role column.
                    </span>
                  </span>
                </li>
              );
            }

            if (fromGroup && !held) {
              return (
                <li key={option.role} className="flex items-start gap-2 text-sm">
                  <Users className="mt-0.5 h-4 w-4 shrink-0 text-navy-700" aria-hidden="true" />
                  <span>
                    <span className="font-medium">{option.role}</span>
                    <span className="block text-xs text-muted-foreground">
                      From a group they are in. Remove them from the group to take it away.
                    </span>
                  </span>
                </li>
              );
            }

            if (option.refusedBecause) {
              return (
                <li key={option.role} className="flex items-start gap-2 text-sm">
                  <Ban
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="font-medium text-muted-foreground">{option.role}</span>
                    {/* The server's sentence, verbatim. */}
                    <span className="block text-xs text-muted-foreground">
                      {option.refusedBecause}
                    </span>
                  </span>
                </li>
              );
            }

            return (
              <li key={option.role} className="flex items-start justify-between gap-3">
                <div className="min-w-0 text-sm">
                  <span className="font-medium">{option.role}</span>
                  {held ? (
                    <span className="block text-xs text-muted-foreground">
                      Added
                      {directGrants.find((entry) => entry.role === option.role)?.note
                        ? ` — ${directGrants.find((entry) => entry.role === option.role)?.note}`
                        : ''}
                    </span>
                  ) : (
                    <span className="block text-xs text-muted-foreground">
                      {option.wouldAdd.length > 0
                        ? `Adds ${option.wouldAdd.length} more ${
                            option.wouldAdd.length === 1 ? 'thing' : 'things'
                          } they may do`
                        : 'Adds nothing they cannot already do'}
                    </span>
                  )}
                </div>

                {held ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => revoke.mutate(option.role)}
                    className="btn-quiet shrink-0 text-xs"
                  >
                    {revoke.isPending && revoke.variables === option.role && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    )}
                    Remove
                  </button>
                ) : (
                  <Tooltip
                    label={
                      option.wouldAdd.length > 0
                        ? option.wouldAdd.slice(0, 6).join(', ') +
                          (option.wouldAdd.length > 6 ? ` and ${option.wouldAdd.length - 6} more` : '')
                        : 'Their current roles already cover everything this would add'
                    }
                    side="left"
                  >
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => grant.mutate(option.role)}
                      className="btn-secondary shrink-0 px-3 py-1.5 text-xs"
                    >
                      {grant.isPending && grant.variables === option.role && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      )}
                      Add
                    </button>
                  </Tooltip>
                )}
              </li>
            );
          })}
        </ul>

        {/*
          The note answers what an audit row cannot: not who changed it, but
          what the arrangement is. "Covers Nyanchwa while Peter is on leave" is
          the difference between a grant somebody can undo confidently in three
          months and one nobody dares touch.
        */}
        <label className="mt-3 block text-xs text-muted-foreground">
          Why, in your own words (optional)
          <input
            className="input mt-1"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Covers Nyanchwa sales while Peter is on leave"
            maxLength={200}
          />
        </label>
      </div>

      {/* Groups, read-only here — they are managed on their own screen. */}
      {groups.length > 0 && (
        <div>
          <p className="text-xs font-semibold label-mono tracking-[0.12em] text-muted-foreground">
            In these groups
          </p>
          <ul className="mt-1.5 space-y-1 text-sm">
            {groups.map((group) => (
              <li key={group.id} className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{group.name}</span>
                <span className="text-xs text-muted-foreground">
                  {group.isActive ? group.roles.join(', ') : 'switched off, grants nothing'}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/access" className="mt-1.5 inline-block text-xs text-navy-700 hover:underline">
            Manage groups
          </Link>
        </div>
      )}

      {error && <p className="text-sm text-danger-600">{error}</p>}

      {!user.onRoster && (
        <p className="text-xs text-muted-foreground">
          {name} has no technician roster entry, which is why the technician role is not offered.
          Add them under People to change that.
        </p>
      )}
    </div>
  );
}
